import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './useSupabaseAuth'
import { SubscriptionService, CREDIT_COSTS } from '../services/subscriptionService'
import { supabase } from '../services/supabaseClient'
import { debounce, SimpleCache } from '../utils/performance'
import { API_CONFIG } from '../constants/apiConfig'

// Error tracking utility
const trackError = (error, context) => {
  console.error(`[${context}] Error:`, error)
  // TODO: Integrate with error tracking service
  // analytics.track('subscription_error', { error: error.message, context })
}

// Performance monitoring utility
const measurePerformance = async (name, fn) => {
  const start = performance.now()
  try {
    const result = await fn()
    const end = performance.now()
    if (API_CONFIG.VERBOSE_LOGGING) {
      console.log(`${name} took ${end - start} milliseconds`)
    }
    return result
  } catch (error) {
    const end = performance.now()
    console.log(`${name} failed after ${end - start} milliseconds`)
    throw error
  }
}

// Cache instance for subscription data
const subscriptionCache = new SimpleCache(API_CONFIG.CACHE_TTL)
const packagesCache = new SimpleCache(API_CONFIG.CACHE_TTL)

export const useSubscription = () => {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load subscription data with enhanced error handling and performance monitoring
  const loadSubscription = useCallback(async () => {
    if (!user?.id) {
      console.warn('No authenticated user found')
      setSubscription(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Verify user session is valid
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.warn('No valid session found')
        setError(new Error('Authentication required'))
        setLoading(false)
        return
      }
      
      // Load subscription with performance monitoring
      const result = await measurePerformance('loadSubscription', async () => {
        return await SubscriptionService.getCurrentSubscription(user.id)
      })
      
      if (result.error) {
        trackError(result.error, 'loadSubscription')
        setError(result.error)
      } else {
        console.log('🔍 Raw subscription data from service:', result.data)
        setSubscription(result.data)
        console.log('Subscription loaded successfully:', result.data?.id || 'No active subscription')
      }
    } catch (err) {
      trackError(err, 'loadSubscription')
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Load available packages with caching
  const loadPackages = useCallback(async () => {
    try {
      // Check cache first
      const cacheKey = 'subscription_packages'
      const cachedPackages = packagesCache.get(cacheKey)
      
      if (cachedPackages) {
        console.log('📦 Using cached packages data')
        setPackages(cachedPackages)
        return
      }
      
      const result = await measurePerformance('loadPackages', async () => {
        return await SubscriptionService.getPackages()
      })
      
      if (result.error) {
        trackError(result.error, 'loadPackages')
        setError(result.error)
      } else {
        const packages = result.data || []
        setPackages(packages)
        // Cache the result
        packagesCache.set(cacheKey, packages)
        console.log('📦 Packages loaded and cached successfully')
      }
    } catch (err) {
      trackError(err, 'loadPackages')
      setError(err)
    }
  }, [])

  // Check if user has enough credits for an action
  const checkCredits = useCallback(async (actionType) => {
    if (!user) return { hasCredits: false, needsUpgrade: true, error: 'User not authenticated' }
    
    const result = await SubscriptionService.checkCredits(user.id, actionType)
    return result
  }, [user])

  // Consume credits for an action
  const consumeCredits = useCallback(async (actionType, metadata = {}) => {
    if (!user) return { success: false, error: 'User not authenticated' }
    
    const result = await SubscriptionService.consumeCredits(user.id, actionType, metadata)
    
    if (result.success) {
      // Update local subscription state
      setSubscription(prev => prev ? {
        ...prev,
        credits_remaining: result.creditsRemaining
      } : null)
    }
    
    return result
  }, [user])

  // Removed checkAgentAccess - no longer needed

  // Create a new subscription transaction
  const createTransaction = useCallback(async (packageId) => {
    if (!user) return { data: null, error: 'User not authenticated' }
    
    return await SubscriptionService.createTransaction(user.id, packageId)
  }, [user])

  // Get transaction by order ID
  const getTransaction = useCallback(async (orderId) => {
    return await SubscriptionService.getTransaction(orderId)
  }, [])

  // Get credit usage history
  const getCreditUsageHistory = useCallback(async (limit = 50) => {
    if (!user) return { data: null, error: 'User not authenticated' }
    
    return await SubscriptionService.getCreditUsageHistory(user.id, limit)
  }, [user])

  // Get transaction history
  const getTransactionHistory = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' }
    
    try {
      const result = await SubscriptionService.getUserTransactionHistory(user.id)
      return result
    } catch (error) {
      console.error('Error fetching transaction history:', error)
      return { success: false, error: error.message }
    }
  }, [user])

  // Memoized computed values for performance
  const memoizedPackages = useMemo(() => {
    return packages.map(pkg => ({
      ...pkg,
      formattedPrice: new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR'
      }).format(pkg.price),
      creditsPerRupiah: (pkg.credits / pkg.price).toFixed(4)
    }))
  }, [packages])

  // Helper functions with memoization
  const hasActiveSubscription = useMemo(() => {
    if (!subscription) {
      return false
    }
    
    const isStatusActive = subscription.status === 'active'
    const isNotExpired = subscription.expires_at && new Date(subscription.expires_at) > new Date()
    
    return isStatusActive && isNotExpired
  }, [subscription])
  
  const creditsRemaining = useMemo(() => 
    subscription?.credits_remaining || 0, [subscription]
  )
  
  const subscriptionExpiry = useMemo(() => 
    subscription?.expires_at, [subscription]
  )
  
  const packageName = useMemo(() => 
    subscription?.subscription_packages?.name, [subscription]
  )

  // Check if subscription is expiring soon (within 7 days)
  const isExpiringSoon = subscription && subscription.expires_at && 
    new Date(subscription.expires_at) - new Date() < 7 * 24 * 60 * 60 * 1000

  // Get credit cost for an action
  const getCreditCost = useCallback((actionType) => {
    return CREDIT_COSTS[actionType] || 0
  }, [])

  // Check if user has access to specific agent
  const checkAgentAccess = useCallback(async (agentType) => {
    if (!user) return { hasAccess: false, needsUpgrade: true, error: 'User not authenticated' }
    
    return await SubscriptionService.checkAgentAccess(user.id, agentType)
  }, [user])

  // Check if user can perform an action (with detailed info)
  const canPerformAction = useCallback(async (actionType) => {
    if (!user) {
      return {
        canPerform: false,
        reason: 'not_authenticated',
        needsUpgrade: true,
        message: 'Anda perlu login terlebih dahulu'
      }
    }

    // Wait for loading to complete with timeout
    let waitCount = 0
    while (loading && waitCount < 10) {
      await new Promise(resolve => setTimeout(resolve, 300))
      waitCount++
    }

    // Force refresh if no subscription after loading
    if (!subscription && !loading) {
      await loadSubscription()
      // Wait a bit for the refresh to complete
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    if (!hasActiveSubscription) {
      // Don't automatically create FREE subscription to avoid affecting other users
      // User needs to have an active subscription to perform actions
      return {
        canPerform: false,
        reason: 'no_subscription',
        needsUpgrade: true,
        message: 'Anda perlu berlangganan untuk menggunakan fitur ini'
      }
    }

    // Check agent access for agent-specific actions
    if (actionType.includes('_agent')) {
      const agentAccess = await checkAgentAccess(actionType)
      if (!agentAccess.hasAccess) {
        return {
          canPerform: false,
          reason: agentAccess.reason,
          needsUpgrade: agentAccess.needsUpgrade,
          packageName: agentAccess.packageName,
          message: agentAccess.message
        }
      }
    }

    const creditCheck = await checkCredits(actionType)
    
    if (!creditCheck.hasCredits) {
      const isFreeUser = creditCheck.packageName === 'FREE'
      
      return {
        canPerform: false,
        reason: 'insufficient_credits',
        needsUpgrade: true,
        creditsRemaining: creditCheck.remainingCredits,
        creditsRequired: creditCheck.requiredCredits,
        packageName: creditCheck.packageName,
        isFreeUser,
        message: isFreeUser 
          ? `Kredit FREE Anda tidak mencukupi. Dibutuhkan ${creditCheck.requiredCredits} kredit, tersisa ${creditCheck.remainingCredits} kredit. Upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.`
          : `Kredit Anda tidak mencukupi. Dibutuhkan ${creditCheck.requiredCredits} kredit, tersisa ${creditCheck.remainingCredits} kredit. Silakan upgrade paket Anda.`
      }
    }

    console.log(`✅ [canPerformAction] Action allowed for ${actionType}`)
    return {
      canPerform: true,
      reason: 'sufficient_credits',
      needsUpgrade: false,
      creditsRemaining: creditCheck.remainingCredits,
      creditsRequired: creditCheck.requiredCredits,
      packageName: creditCheck.packageName
    }
  }, [user, hasActiveSubscription, checkCredits, checkAgentAccess, loading, subscription, loadSubscription])

  // Debounced version of loadSubscription to prevent excessive API calls
  const debouncedLoadSubscription = useCallback(
    debounce(loadSubscription, 500),
    [loadSubscription]
  )

  // Load data on mount and when user changes
  useEffect(() => {
    // Load packages immediately (they don't change often)
    loadPackages()
    
    // Load subscription with debouncing
    if (user?.id) {
      debouncedLoadSubscription()
    }
  }, [user?.id, loadPackages, debouncedLoadSubscription])
  
  // Manual refresh function
  const refreshSubscription = useCallback(async () => {
    console.log('🔄 Manual subscription refresh triggered')
    await loadSubscription()
  }, [loadSubscription])

  // Periodic refresh for subscription data
  useEffect(() => {
    if (!user?.id || !API_CONFIG.SUBSCRIPTION_REFRESH_INTERVAL) return
    
    const interval = setInterval(() => {
      console.log('🔄 Periodic subscription refresh')
      loadSubscription()
    }, API_CONFIG.SUBSCRIPTION_REFRESH_INTERVAL)
    
    return () => clearInterval(interval)
  }, [user?.id, loadSubscription])

  // Listen for manual refresh events
  useEffect(() => {
    const handleRefreshEvent = () => {
      console.log('🔄 Manual refresh event received')
      refreshSubscription()
    }

    window.addEventListener('refreshSubscription', handleRefreshEvent)
    return () => window.removeEventListener('refreshSubscription', handleRefreshEvent)
  }, [refreshSubscription])

  // Health check function
  const healthCheck = useCallback(async () => {
    try {
      // Simple health check - verify we can load subscription data
      if (!user?.id) {
        return { healthy: false, error: 'No authenticated user' }
      }
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        return { healthy: false, error: 'No valid session' }
      }
      
      // Test subscription service connection
      const result = await SubscriptionService.getCurrentSubscription(user.id)
      return { 
        healthy: !result.error, 
        error: result.error?.message || null,
        data: result.data
      }
    } catch (error) {
      return { healthy: false, error: error.message }
    }
  }, [user])

  return {
    // State
    subscription,
    packages: memoizedPackages,
    loading,
    error,
    
    // Computed values
    hasActiveSubscription,
    creditsRemaining,
    subscriptionExpiry,
    packageName,
    isExpiringSoon,
    
    // Essential functions only
    loadSubscription,
    loadPackages,
    checkCredits,
    consumeCredits,
    canPerformAction,
    checkAgentAccess,
    getCreditCost,
    createTransaction,
    getTransaction,
    getCreditUsageHistory,
    getTransactionHistory,
    healthCheck,
    refreshSubscription,
    
    // Utilities
    clearError: () => setError(null),
    refreshData: () => {
      // Clear cache before refreshing
      subscriptionCache.clear()
      packagesCache.clear()
      console.log('🗑️ Cache cleared, refreshing data...')
      
      loadSubscription()
      loadPackages()
    }
  }
}

export default useSubscription